import { toast } from 'sonner'

import {
  TRIAL_UNAVAILABLE_ERROR_CODE,
  WORKSPACE_READ_ONLY_ERROR_CODE,
} from '@scrambl/shared'

/**
 * Client side of the no-card trial read-only lock
 * (docs/PLAN-no-card-trials.md): the API answers writes to a locked
 * workspace with 403 `WORKSPACE_READ_ONLY`.
 *
 * Deliberately dependency-free (no stores, no hooks, no api client): the
 * api client calls in here, so anything heavier would be an import cycle.
 */

const READ_ONLY_TOAST_ID = 'workspace-read-only'

function errorCode(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return undefined
  const record = value as {
    code?: unknown
    data?: unknown
    response?: { data?: unknown }
  }
  if (typeof record.code === 'string') return record.code
  const data = record.data ?? record.response?.data
  if (data && typeof data === 'object') {
    const code = (data as { code?: unknown }).code
    if (typeof code === 'string') return code
  }
  return undefined
}

/** An ApiError (or its parsed payload) for a write refused by the lock. */
export function isWorkspaceReadOnlyError(error: unknown): boolean {
  return errorCode(error) === WORKSPACE_READ_ONLY_ERROR_CODE
}

/** The org cannot start a no-card trial (flag off or trial already used). */
export function isTrialUnavailableError(error: unknown): boolean {
  return errorCode(error) === TRIAL_UNAVAILABLE_ERROR_CODE
}

const listeners = new Set<() => void>()

/** Called when a write was refused; the trial banner refreshes billing state. */
export function subscribeWorkspaceReadOnly(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * One friendly toast per burst of blocked writes (sonner dedupes by id), then
 * let subscribers refresh so the read-only banner appears if the lock started
 * while the page was open.
 */
export function notifyWorkspaceReadOnly(): void {
  toast.error('This workspace is read-only', {
    id: READ_ONLY_TOAST_ID,
    description:
      'The trial ended, so changes are paused. Your data is safe — an owner or admin can add a card to turn editing back on.',
  })
  for (const listener of listeners) listener()
}
